import React, { useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TextField from "@mui/material/TextField";

import Input from "../../form/Input";
import RestockSubQuantityField from "./RestockSubQuantityField";
import CurrentQuantityView from "./CurrentQuantityView";
import LocationQuantityField from "./LocationQuantityField";
import DeleteLocation from "./DeleteLocation";
import useAuth from "src/hooks/useAuth";
import StorageLocationAutocomplete from "../../../views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import DuplicateLocationsModal from "./DuplicateLocationsModal";

function LocationRow({
  index,
  autoOpen,
  number,
  multiSelect,
  location,
  setFieldValue,
  selectedLocations,
  onCheckboxChange,
  openRemoveLocation,
  setOpenRemoveLocation,
  onDeleteLocations,
  displayShipmentId,
  displayDeleteLocation = true,
  displayDuplicate,
  projectId,
  disableQuantity,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const locationQtyRef = useRef(null);

  return (
    <React.Fragment>
      <Grid sx={{ display: "flex", alignItems: "center" }} size={{ xs: 12, sm: 6 }}>
        {multiSelect && (
          <Checkbox
            checked={selectedLocations.includes(index)}
            onChange={() => onCheckboxChange(index)}
          />
        )}
        <Box width="100%">
          <StorageLocationAutocomplete
            autoOpen={autoOpen}
            projectId={projectId}
            index={index}
            name="locations"
            value={location}
            onChange={(values) => {
              const finalValues = {
                ...values,
                mobile_warehouse_location:
                  values.mobile_warehouse_location !== undefined
                    ? values.mobile_warehouse_location
                    : location.mobile_warehouse_location,
              };

              Object.entries(finalValues).forEach(([key, value]) => {
                setFieldValue(`locations.${index}.${key}`, value);
              });

              if (finalValues.warehouse_locations?.length === 1) {
                setTimeout(() => {
                  const input = locationQtyRef.current?.querySelector("input");
                  if (input) {
                    input.focus();
                  }
                }, 100);
              }
            }}
          />
        </Box>
      </Grid>
      {settings.simple_inventory_modal ? (
        <>
          {displayShipmentId && (
            <Grid sx={{ display: "flex", alignItems: "center" }} size={{ xs: 5, sm: 2 }}>
              <Input size="large" name={`locations.${index}.shipment_id`} label="Shipment ID" />
            </Grid>
          )}
          <Grid
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
            size={{ xs: displayShipmentId ? 7 : 12, sm: displayShipmentId ? 4 : 6 }}
          >
            <Box
              ref={locationQtyRef}
              sx={(theme) => ({
                flex: { xs: 1, sm: "0 1 auto" },
                [theme.breakpoints.down("sm")]: {
                  "& .MuiFormControl-root, & .MuiTextField-root": { width: "100%" },
                },
              })}
            >
              {disableQuantity ? (
                <TextField
                  disabled
                  size="large"
                  label="Location QTY"
                  value={location.sub_qty + (location.restock_sub_qty || 0)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", sm: 110 } }}
                />
              ) : (
                <LocationQuantityField
                  size="large"
                  name={`locations.${index}.restock_sub_qty`}
                  location={location}
                  disabled={disableQuantity}
                />
              )}
            </Box>
            {displayDeleteLocation && (
              <DeleteLocation
                index={index}
                openRemoveLocation={openRemoveLocation}
                setOpenRemoveLocation={setOpenRemoveLocation}
                onDeleteLocations={onDeleteLocations}
              />
            )}
            {displayDuplicate && (
              <Tooltip arrow title="Duplicate" TransitionComponent={Zoom}>
                <IconButton aria-label="duplicate" onClick={() => setOpenDuplicateModal(true)}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        </>
      ) : (
        <>
          {location.sub_qty !== 0 && (
            <Grid sx={{ display: "flex", alignItems: "center" }} size={2}>
              <CurrentQuantityView name={`locations.${index}.sub_qty`} />
            </Grid>
          )}
          <Grid sx={{ display: "flex", alignItems: "center" }} size={4}>
            {disableQuantity ? (
              <TextField
                fullWidth
                disabled
                size="small"
                label={`Restock Sub QTY ${number}`}
                value={location.restock_sub_qty || 0}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <RestockSubQuantityField
                name={`locations.${index}.restock_sub_qty`}
                label={`Restock Sub QTY ${number}`}
                disabled={disableQuantity}
              />
            )}
          </Grid>
        </>
      )}
      <Grid sx={{ display: { xs: "block", sm: "none" } }} size={12}>
        <Divider sx={{ my: 0.5 }} />
      </Grid>
      {settings.simple_inventory_modal && openDuplicateModal && (
        <DuplicateLocationsModal index={index} onClose={() => setOpenDuplicateModal(false)} />
      )}
    </React.Fragment>
  );
}

export default React.memo(LocationRow);
